import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { isPinned, togglePin, isPinnable, ROUTE_LABELS } from '@/utils/recentlyVisited';

/**
 * Floating "Pin this page" star icon.
 * - Fixed bottom-right on mobile/tablet (lg:hidden so desktop isn't cluttered).
 * - Only renders when the current path is pinnable (has a known label).
 * - Hides when the mobile drawer is open (caller can pass drawerOpen).
 */
export default function PinPageButton({ drawerOpen = false }) {
  const location = useLocation();
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    setPinned(isPinned(location.pathname));
    const listener = () => setPinned(isPinned(location.pathname));
    window.addEventListener('dowurk:pins-changed', listener);
    return () => window.removeEventListener('dowurk:pins-changed', listener);
  }, [location.pathname]);

  if (drawerOpen) return null;
  if (!isPinnable(location.pathname)) return null;

  const label = ROUTE_LABELS[location.pathname]?.label || 'this page';

  const handleClick = () => {
    const result = togglePin(location.pathname);
    if (result === null) {
      toast.error('Pin limit reached (8). Unpin something first.');
    } else if (result === true) {
      toast.success(`📌 Pinned ${label}`);
    } else {
      toast.success('Unpinned');
    }
    setPinned(Boolean(result));
  };

  return (
    <button
      onClick={handleClick}
      aria-label={pinned ? 'Unpin this page' : 'Pin this page'}
      title={pinned ? 'Unpin from quick access' : 'Pin for quick access'}
      data-testid="pin-page-btn"
      className={`lg:hidden fixed bottom-20 right-4 z-40 w-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95 ${
        pinned
          ? 'bg-gradient-to-br from-[#FFC107] to-[#FF9800] text-white'
          : 'bg-white border border-gray-300 text-gray-500 hover:text-[#FFC107] hover:border-[#FFC107]'
      }`}
    >
      <Star className={`w-5 h-5 ${pinned ? 'fill-white' : ''}`} />
    </button>
  );
}
